import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../auth/AuthProvider';
import { buildCoreAssetUrl } from '../../../config/environment';
import { CreatePlatformMemberInput, platformCoreNetworkApi, PlatformMember } from '../../../api/platform/coreNetwork';
import { useAsyncData } from '../hooks/useAsyncData';
import {
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  FeedbackBanner,
  FieldGroup,
  FilterGrid,
  InlineActions,
  LoadingState,
  MvpCard,
  MvpTalentLayout,
  PaginationControls,
  StatusBadge,
} from '../components/MvpTalentUi';
import { formatDate, formatDateTime, formatList, toInitials } from '../utils/format';

const MEMBER_TYPE_OPTIONS = ['provider', 'designer', 'decorator', 'vendor', 'partner'];
const GLOBAL_STATUS_OPTIONS = ['pending', 'active', 'rejected', 'suspended', 'disabled'];
const NETWORK_TYPE_OPTIONS = ['design', 'decoration', 'logistics', 'software', 'data_entry'];
const CAPABILITY_OPTIONS = ['artwork-proof', 'digitizing', 'mockups', 'artwork', 'vector-art', 'proof-mockup'];

const createDefaultFormState = () => ({
  displayName: '',
  email: '',
  phone: '',
  companyName: '',
  memberType: 'provider',
  profileType: 'platform_managed',
  providerKey: '',
  profileImageUrl: '',
  networkTypesJson: [] as string[],
  capabilityTypes: [] as string[],
  timezone: '',
  country: '',
  globalStatus: 'active',
  reason: '',
});

const normalizeOptionalText = (value: string) => {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const TalentListPage: React.FC = () => {
  const { isAdministrator } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);
  const [dialog, setDialog] = useState<{ member: PlatformMember; action: 'approve' | 'reject' | 'suspend' | 'reactivate' | 'revokePortal' } | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState(createDefaultFormState);

  const filters = useMemo(() => ({
    page: Number(searchParams.get('page') || 1),
    limit: 10,
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || '',
    verificationStatus: searchParams.get('verificationStatus') || '',
    capability: searchParams.get('capability') || '',
    instanceId: searchParams.get('instanceId') || '',
    portalStatus: searchParams.get('portalStatus') || '',
    createdFrom: searchParams.get('createdFrom') || '',
    lastActiveFrom: searchParams.get('lastActiveFrom') || '',
  }), [searchParams]);

  const { data, loading, error, reload } = useAsyncData(
    (signal) => platformCoreNetworkApi.listMembers(filters, { signal }),
    [filters.page, filters.search, filters.status, filters.verificationStatus, filters.capability, filters.instanceId, filters.portalStatus, filters.createdFrom, filters.lastActiveFrom]
  );

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('page');
    setSearchParams(next);
  };

  const runMutation = async (member: PlatformMember, action: NonNullable<typeof dialog>['action'], reason: string) => {
    try {
      if (action === 'revokePortal') {
        await platformCoreNetworkApi.revokePortalAccess(member.id, reason);
      } else {
        const status = action === 'approve' ? 'active' : action === 'reject' ? 'rejected' : action === 'suspend' ? 'suspended' : 'active';
        await platformCoreNetworkApi.updateMemberStatus(member.id, { status, reason });
      }
      setFeedback({ tone: 'success', message: `${member.displayName} updated successfully.` });
      await reload();
    } catch (mutationError: any) {
      setFeedback({ tone: 'error', message: mutationError?.message || 'Unable to update the talent profile.' });
      throw mutationError;
    }
  };

  const toggleMultiValue = (key: 'networkTypesJson' | 'capabilityTypes', value: string) => {
    setCreateForm((current) => {
      const currentValues = current[key];
      const nextValues = currentValues.includes(value)
        ? currentValues.filter((entry) => entry !== value)
        : [...currentValues, value];
      return { ...current, [key]: nextValues };
    });
  };

  const handleCreateChange = (key: keyof ReturnType<typeof createDefaultFormState>, value: string) => {
    setCreateForm((current) => ({ ...current, [key]: value }));
  };

  const handleCreateTalent = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCreateSubmitting(true);
    setCreateError(null);

    const payload: CreatePlatformMemberInput = {
      displayName: createForm.displayName.trim(),
      email: normalizeOptionalText(createForm.email),
      phone: normalizeOptionalText(createForm.phone),
      companyName: normalizeOptionalText(createForm.companyName),
      memberType: createForm.memberType,
      profileType: normalizeOptionalText(createForm.profileType),
      providerKey: normalizeOptionalText(createForm.providerKey),
      profileImageUrl: normalizeOptionalText(createForm.profileImageUrl),
      networkTypesJson: createForm.networkTypesJson,
      capabilityTypes: createForm.capabilityTypes,
      timezone: normalizeOptionalText(createForm.timezone),
      country: normalizeOptionalText(createForm.country),
      globalStatus: createForm.globalStatus,
      reason: normalizeOptionalText(createForm.reason),
    };

    try {
      const member = await platformCoreNetworkApi.createMember(payload);
      setFeedback({ tone: 'success', message: `${member.displayName} created successfully.` });
      setCreateModalOpen(false);
      setCreateForm(createDefaultFormState());
      await reload();
      navigate(`/command-center/mvp-talent/talent/${member.id}`, {
        state: { feedback: { tone: 'success', message: `${member.displayName} created successfully.` } },
      });
    } catch (mutationError: any) {
      setCreateError(mutationError?.message || 'Unable to create the talent profile.');
    } finally {
      setCreateSubmitting(false);
    }
  };

  return (
    <MvpTalentLayout
      title="Talent Directory"
      subtitle="Global talent records and portal-access controls."
      actions={
        isAdministrator ? (
          <button
            type="button"
            className="mvp-button"
            onClick={() => {
              setCreateError(null);
              setCreateModalOpen(true);
            }}
          >
            Create Talent
          </button>
        ) : null
      }
    >
      {feedback ? <FeedbackBanner tone={feedback.tone} message={feedback.message} onDismiss={() => setFeedback(null)} /> : null}
      <MvpCard title="Filters">
        <FilterGrid>
          <FieldGroup label="Search">
            <input value={filters.search} onChange={(event) => updateFilter('search', event.target.value)} placeholder="Name, email, company" />
          </FieldGroup>
          <FieldGroup label="Status">
            <select value={filters.status} onChange={(event) => updateFilter('status', event.target.value)}>
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="rejected">Rejected</option>
              <option value="suspended">Suspended</option>
              <option value="disabled">Disabled</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Verification">
            <select value={filters.verificationStatus} onChange={(event) => updateFilter('verificationStatus', event.target.value)}>
              <option value="">All</option>
              <option value="verified">Verified</option>
              <option value="unverified">Unverified</option>
            </select>
          </FieldGroup>
          <FieldGroup label="Capability">
            <input value={filters.capability} onChange={(event) => updateFilter('capability', event.target.value)} placeholder="artwork-proof" />
          </FieldGroup>
          <FieldGroup label="Instance ID">
            <input value={filters.instanceId} onChange={(event) => updateFilter('instanceId', event.target.value)} placeholder="Instance ID" />
          </FieldGroup>
          <FieldGroup label="Portal status">
            <select value={filters.portalStatus} onChange={(event) => updateFilter('portalStatus', event.target.value)}>
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
              <option value="not_enabled">Not enabled</option>
            </select>
          </FieldGroup>
        </FilterGrid>
      </MvpCard>

      {loading ? <LoadingState label="Loading talent directory..." /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}
      {data ? (
        <MvpCard title="Talent">
          {data.rows.length ? (
            <>
              <DataTable columns={['Talent', 'Status', 'Verification', 'Capabilities', 'Memberships', 'Portal', 'Last active', 'Created', 'Actions']}>
                {data.rows.map((member) => (
                  <tr key={member.id}>
                    <td>
                      <div className="mvp-avatar-row">
                        <div className="mvp-avatar">
                          {member.profileImageUrl ? <img src={buildCoreAssetUrl(member.profileImageUrl)} alt={member.displayName} /> : toInitials(member.displayName, member.companyName)}
                        </div>
                        <div>
                          <div><Link to={`/command-center/mvp-talent/talent/${member.id}`}>{member.displayName}</Link></div>
                          <div>{member.companyName || member.email || '-'}</div>
                        </div>
                      </div>
                    </td>
                    <td><StatusBadge status={member.globalStatus} /></td>
                    <td><StatusBadge status={member.verificationStatus} /></td>
                    <td>{formatList(member.capabilityTypes)}</td>
                    <td>{member.activeMembershipCount || 0} active / {member.membershipCount || 0} total</td>
                    <td><StatusBadge status={member.portalStatus} /></td>
                    <td>{formatDateTime(member.portalLastLoginAt)}</td>
                    <td>{formatDate(member.createdAt)}</td>
                    <td>
                      {isAdministrator ? (
                        <InlineActions>
                          <button type="button" className="mvp-button mvp-button--ghost" onClick={() => setDialog({ member, action: 'approve' })}>Approve</button>
                          <button type="button" className="mvp-button mvp-button--ghost" onClick={() => setDialog({ member, action: 'suspend' })}>Suspend</button>
                          <button type="button" className="mvp-button mvp-button--ghost" onClick={() => setDialog({ member, action: 'revokePortal' })}>Revoke portal</button>
                        </InlineActions>
                      ) : <span>Restricted</span>}
                    </td>
                  </tr>
                ))}
              </DataTable>
              <PaginationControls page={data.pagination.page} totalPages={data.pagination.pages} onPageChange={(nextPage) => updateFilter('page', String(nextPage))} />
            </>
          ) : <EmptyState title="No talent found" message="Try adjusting the current filters or search term." />}
        </MvpCard>
      ) : null}

      <ConfirmDialog
        open={Boolean(dialog)}
        title={dialog ? `${dialog.action === 'revokePortal' ? 'Revoke portal access' : 'Update talent status'}: ${dialog.member.displayName}` : ''}
        description={dialog?.action === 'revokePortal' ? 'This immediately revokes current portal access and records a platform audit event.' : 'This action is recorded on the Elite Core platform audit trail and requires a reason.'}
        confirmLabel={dialog?.action === 'revokePortal' ? 'Revoke portal access' : 'Apply change'}
        confirmTone={dialog?.action === 'suspend' || dialog?.action === 'reject' || dialog?.action === 'revokePortal' ? 'danger' : 'default'}
        onClose={() => setDialog(null)}
        onConfirm={async ({ reason }) => {
          if (!dialog) return;
          await runMutation(dialog.member, dialog.action, reason);
        }}
      />

      {createModalOpen ? (
        <div className="mvp-dialog-backdrop" role="presentation" onClick={() => !createSubmitting && setCreateModalOpen(false)}>
          <div className="mvp-dialog mvp-dialog--wide" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <h3>Create Talent</h3>
            <p>Create a global talent profile for Core Network. Instance memberships remain managed separately.</p>
            {createError ? <FeedbackBanner tone="error" message={createError} /> : null}
            <form className="mvp-form-grid" onSubmit={handleCreateTalent}>
              <FieldGroup label="Display name *">
                <input
                  value={createForm.displayName}
                  onChange={(event) => handleCreateChange('displayName', event.target.value)}
                  placeholder="MVP"
                  required
                />
              </FieldGroup>
              <FieldGroup label="Email">
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(event) => handleCreateChange('email', event.target.value)}
                  placeholder="provider@example.com"
                />
              </FieldGroup>
              <FieldGroup label="Phone">
                <input value={createForm.phone} onChange={(event) => handleCreateChange('phone', event.target.value)} />
              </FieldGroup>
              <FieldGroup label="Company name">
                <input value={createForm.companyName} onChange={(event) => handleCreateChange('companyName', event.target.value)} placeholder="Elite Core" />
              </FieldGroup>
              <FieldGroup label="Member type *">
                <select value={createForm.memberType} onChange={(event) => handleCreateChange('memberType', event.target.value)}>
                  {MEMBER_TYPE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </FieldGroup>
              <FieldGroup label="Profile type">
                <input value={createForm.profileType} onChange={(event) => handleCreateChange('profileType', event.target.value)} />
              </FieldGroup>
              <FieldGroup label="Provider key">
                <input value={createForm.providerKey} onChange={(event) => handleCreateChange('providerKey', event.target.value)} placeholder="mvp" />
              </FieldGroup>
              <FieldGroup label="Profile image URL">
                <input value={createForm.profileImageUrl} onChange={(event) => handleCreateChange('profileImageUrl', event.target.value)} placeholder="https://..." />
              </FieldGroup>
              <FieldGroup label="Timezone">
                <input value={createForm.timezone} onChange={(event) => handleCreateChange('timezone', event.target.value)} placeholder="America/Edmonton" />
              </FieldGroup>
              <FieldGroup label="Country">
                <input value={createForm.country} onChange={(event) => handleCreateChange('country', event.target.value)} placeholder="Canada" />
              </FieldGroup>
              <FieldGroup label="Global status">
                <select value={createForm.globalStatus} onChange={(event) => handleCreateChange('globalStatus', event.target.value)}>
                  {GLOBAL_STATUS_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </FieldGroup>
              <FieldGroup label="Administrative reason">
                <textarea value={createForm.reason} onChange={(event) => handleCreateChange('reason', event.target.value)} rows={3} />
              </FieldGroup>
              <FieldGroup label="Network types">
                <div className="mvp-checkbox-grid">
                  {NETWORK_TYPE_OPTIONS.map((option) => (
                    <label key={option} className="mvp-checkbox">
                      <input
                        type="checkbox"
                        checked={createForm.networkTypesJson.includes(option)}
                        onChange={() => toggleMultiValue('networkTypesJson', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </FieldGroup>
              <FieldGroup label="Capabilities">
                <div className="mvp-checkbox-grid">
                  {CAPABILITY_OPTIONS.map((option) => (
                    <label key={option} className="mvp-checkbox">
                      <input
                        type="checkbox"
                        checked={createForm.capabilityTypes.includes(option)}
                        onChange={() => toggleMultiValue('capabilityTypes', option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </FieldGroup>
              <div className="mvp-dialog__actions mvp-form-grid__actions">
                <button type="button" className="mvp-button mvp-button--ghost" onClick={() => setCreateModalOpen(false)} disabled={createSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="mvp-button" disabled={createSubmitting || !createForm.displayName.trim()}>
                  {createSubmitting ? 'Creating...' : 'Create Talent'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </MvpTalentLayout>
  );
};

export default TalentListPage;
